<template>
  <!-- The process first, then its activities (US 9369 + 9371). Shown on the process page and on its activities. -->
  <div>
  <V2ParentSwitcher v-if="row && row.parent"
                    :parent="row.parent"
                    :children="children"
                    :selected="selected"
                    :parent-kind="$t('parent_switcher.process.kind')"
                    :parent-sub="$t('parent_switcher.process.sub')"
                    :parent-warn-text="$t('parent_switcher.process.warn')"
                    parent-icon="sitemap"
                    child-icon="cube"
                    :child-label="$t('parent_switcher.process.children')"
                    :can-add="canAdd"
                    :add-label="$t('parent_switcher.process.add')"
                    :add-first-label="$t('parent_switcher.process.add_first')"
                    qid="process-switcher"
                    @select="open"
                    @add="add"></V2ParentSwitcher>
  <!-- Add an activity to this process, as on the processing map -->
  <ActivityCreateForm v-if="canAdd" ref="createActivity" hideButton @reloadContent="added"></ActivityCreateForm>
  <!-- A shared activity: decide where it lives (US 9369 + 9371) -->
  <SharedLinkPanel ref="shared" kind="activity" @decided="decided"></SharedLinkPanel>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2ParentSwitcher from '@/components/V2ParentSwitcher.vue';
  import SharedLinkPanel from '@/components/SharedLinkPanel.vue';
  import ActivityCreateForm from '@/views/Activities/ActivityCreateForm.vue';

  export default {
    name: 'ProcessSwitcher',
    components: { V2ParentSwitcher, SharedLinkPanel, ActivityCreateForm },
    props: {
      // The page it's on: a process, or one of its activities
      process: { type: String, default: '' },
      activity: { type: String, default: '' }
    },
    data() {
      return {
        row: null
      };
    },
    computed: {
      canAdd() {
        return this.isGranted(this.permissions.ACTIVITY_CREATE);
      },
      selected() {
        return this.row && this.row.selected ? this.row.selected : 'parent';
      },
      children() {
        return (this.row ? this.row.children : []).map(child => Object.assign({}, child, {
          sub: this.sub(child),
          warnText: child.without_retention ? this.$t('parent_switcher.activity.without_retention') : ''
        }));
      }
    },
    watch: {
      process() { this.load(); },
      activity() { this.load(); }
    },
    created() {
      this.load();
    },
    methods: {
      // Data subjects · systems, as on the processing map
      sub(child) {
        let parts = [child.data_subjects.slice(0, 2).join(', '), child.systems.slice(0, 2).join(', ')].filter(part => part);
        return parts.length ? parts.join(' · ') : this.$t('parent_switcher.activity.nothing');
      },
      load() {
        let url = this.activity ? 'activities/' + this.activity + '/switcher' : 'processes/' + this.process + '/switcher';
        if (!this.activity && !this.process) {
          return;
        }
        let self = this;
        piincHttp.get(url).then(function(response) {
          self.row = response.data;
          self.$emit('loaded', self.row);
        }, function() {
          self.row = null;
          self.$emit('loaded', null);
        });
      },
      reload() {
        this.load();
      },
      // The process and its activities are one page: /processes/view/:slug and /processes/view/:process/activities/:slug.
      // view=process keeps a process with one activity on the process, instead of opening the activity.
      open(child) {
        if (child === 'parent') {
          if (this.activity) {
            this.$router.push({ name: 'processes.view', params: { slug: this.row.parent.slug }, query: { view: 'process' } });
          }
          return;
        }
        if (child.shared) {
          this.$refs.shared.show(child, { name: child.lives_in }, this.row.parent);
          return;
        }
        if (child.id !== this.selected) {
          this.$router.push({ name: 'processes.activity', params: { process: this.row.parent.slug, slug: child.slug } });
        }
      },
      add() {
        this.$refs.createActivity.open(this.row.parent);
      },
      // A new activity: it joins the row, and the page shows the new count
      added() {
        this.load();
        this.$emit('changed');
      },
      // Kept in its own process: it leaves this row. Moved here or split: open it (or its copy) here.
      decided(event) {
        if (event.action === 'keep') {
          this.load();
          this.$emit('changed');
          return;
        }
        let slug = event.action === 'split' ? event.result.copy.slug : event.item.slug;
        this.$router.push({ name: 'processes.activity', params: { process: event.here.slug, slug: slug } });
      }
    }
  };
</script>
